import { FC, Fragment, useState } from 'react';
import { Avatar, Button, Card, Col, Descriptions } from 'antd';
import { LockOutlined, ProfileOutlined, UserOutlined } from '@ant-design/icons';
import { getInitialTheme } from '@/libs/antd/theme';
import { useUserData } from '@/libs/fleet-api/hooks';
import { unauthenticate } from '@/libs/fleet-api/services/users';
import CardPanel from '@/components/CardPanel';
import FormBuilder from '@/components/FormBuilder';
import { useApp } from '@/App';

const AccountPage: FC = () => {
	const userData = useUserData();
	const { navigate } = useApp();
	const userAvatar = userData && getInitialTheme(userData.fullName);
	const [activeTabKey, setActiveTabKey] = useState<
		'profile' | 'update-profile' | 'update-password'
	>('profile');

	const renderActiveTab = {
		'profile': () => (
			<Card.Meta
				avatar={
					<Avatar
						size={46}
						style={userAvatar?.style}
					>
						{userAvatar?.initial ?? <UserOutlined />}
					</Avatar>
				}
				title={userData?.fullName}
				description={
					<Descriptions
						column={1}
						size="small"
						colon={false}
						labelStyle={{
							width: 120,
						}}
					>
						<Descriptions.Item label="Role">{userData?.role}</Descriptions.Item>
						<Descriptions.Item label="Username">
							{userData?.userName}
						</Descriptions.Item>
						<Descriptions.Item label="Email">
							{userData?.email}
						</Descriptions.Item>
						<Descriptions.Item label="Group">
							{userData?.groupName}
						</Descriptions.Item>
						<Descriptions.Item label="Status">
							{userData?.isActive ? 'Active' : 'Inactive'}
						</Descriptions.Item>
						<Descriptions.Item label="Menu Access">
							{userData?.menus.map((menu) => menu.title).join(', ')}
						</Descriptions.Item>
					</Descriptions>
				}
			/>
		),
		'update-profile': () => (
			<FormBuilder
				fields={[
					{
						name: 'userName',
						label: 'Username',
						type: 'text',
						config: {
							placeholder: 'Username',
						},
					},
					{
						name: 'fullName',
						label: 'Fullname',
						type: 'text',
						config: {
							placeholder: 'Fullname',
						},
					},
					{
						name: 'email',
						label: 'Email',
						type: 'text',
						config: {
							placeholder: 'Email',
						},
					},
				]}
			>
				<Col>
					<Button type="primary">Update Profile</Button>
				</Col>
			</FormBuilder>
		),
		'update-password': () => (
			<FormBuilder
				fields={[
					{
						name: 'password',
						label: 'Password',
						type: 'password',
						config: {
							placeholder: 'Password',
						},
					},
					{
						name: 'confirmPassword',
						label: 'Confirm Password',
						type: 'password',
						config: {
							placeholder: 'Confirm Password',
						},
					},
				]}
			>
				<Col>
					<Button type="primary">Update Password</Button>
				</Col>
			</FormBuilder>
		),
	}[activeTabKey];

	return (
		<Fragment>
			<CardPanel
				activeTabKey={activeTabKey}
				onTabChange={(key) => setActiveTabKey(key as typeof activeTabKey)}
				tabList={[
					{
						key: 'profile',
						icon: <UserOutlined />,
					},
					{
						key: 'update-profile',
						icon: <ProfileOutlined />,
					},
					{
						key: 'update-password',
						icon: <LockOutlined />,
					},
				]}
				tabBarExtraContent={
					<Button
						type="primary"
						danger
						onClick={async () => {
							await unauthenticate({ logout: true });
							navigate('LoginPage');
						}}
					>
						Logout
					</Button>
				}
				styles={{
					header: {
						paddingRight: 12,
					},
				}}
				style={{
					maxWidth: 460,
					margin: '32px auto',
				}}
			>
				<CardPanel.Content>{renderActiveTab()}</CardPanel.Content>
			</CardPanel>
		</Fragment>
	);
};

export default AccountPage;
